import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Item } from '@react-stately/collections'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Select } from 'components/Select/Select'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { Spinner } from 'common/Spinner/Spinner'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'
import { Pause, Play3, UserProfileAdd, Upload } from 'blaze-ui/icons/20'
import { AvatarCatalogEntry, AvatarVoicePreset, AvatarVoiceType, BrandKitAvatar } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useAvatarVoicePresets } from 'hooks/queries/brandKits/useAvatarVoicePresets'
import { useAvatarCatalog } from 'hooks/queries/brandKits/useAvatarCatalog'
import { useCreateBrandKitAvatar } from 'hooks/queries/brandKits/useCreateBrandKitAvatar'
import { useUpdateBrandKitAvatar } from 'hooks/queries/brandKits/useUpdateBrandKitAvatar'
import {
  REPRESENTATION_AGE_OPTIONS,
  REPRESENTATION_ETHNICITY_OPTIONS,
} from 'screens/BrandKits/components/ContentPreferences/constants'
import { AvatarDraft } from 'components/AiAvatarAnnouncementModal/types'
import { verificationFailureCopy } from './VerificationPill'
import { VoiceRecorder } from './VoiceRecorder'
import Styles from './AddAvatarModal.module.scss'

type VoiceMode = 'preset' | 'upload' | 'record'

const voiceModeForType = (voiceType: AvatarVoiceType | undefined): VoiceMode => {
  if (voiceType === 'record') return 'record'
  if (voiceType === 'upload') return 'upload'
  return 'preset'
}

const voicePresetLabel = (preset: AvatarVoicePreset) =>
  `${preset.display_name} (${preset.gender === 'female' ? 'F' : 'M'})`

const formTitle = (isEditing: boolean, isDraftAdd: boolean) => {
  if (isEditing) return 'Edit avatar'
  if (isDraftAdd) return 'Add avatar'
  return 'New avatar'
}

const submitLabel = (isEditing: boolean, isDraftAdd: boolean) => {
  if (isEditing) return 'Save changes'
  if (isDraftAdd) return 'Add avatar'
  return 'Create avatar'
}

interface InitialValues {
  name: string
  personaNote: string
  age: string
  ethnicity: string
  mediaFileId?: number
  photoUrl: string
  enabled: boolean
  catalogKey: string | null
  voiceMode: VoiceMode
  voicePreset: string
  uploadMediaFileId?: number
  uploadFileName: string
  uploadSampleUrl: string
  recordMediaFileId?: number
}

const voiceModeForDraft = (draft: AvatarDraft): VoiceMode => {
  if (!draft.voicePreset && draft.voiceMediaFileId) return 'upload'
  return 'preset'
}

const initialFromDraft = (draft: AvatarDraft): InitialValues => {
  const voiceMode = voiceModeForDraft(draft)
  return {
    name: draft.name,
    personaNote: draft.personaNote,
    age: draft.age ?? '',
    ethnicity: draft.ethnicity ?? '',
    mediaFileId: draft.mediaFileId,
    photoUrl: draft.photoUrl,
    enabled: true,
    catalogKey: draft.catalogKey ?? null,
    voiceMode,
    voicePreset: draft.voicePreset ?? '',
    uploadMediaFileId: voiceMode === 'upload' ? draft.voiceMediaFileId ?? undefined : undefined,
    uploadFileName: voiceMode === 'upload' ? 'Voice sample' : '',
    uploadSampleUrl: voiceMode === 'upload' ? draft.voiceSampleUrl ?? '' : '',
    recordMediaFileId: undefined,
  }
}

const initialFromAvatar = (avatar?: BrandKitAvatar): InitialValues => {
  const voice = avatar?.voice
  return {
    name: avatar?.name ?? '',
    personaNote: avatar?.persona_note ?? '',
    age: avatar?.age ?? '',
    ethnicity: avatar?.ethnicity ?? '',
    mediaFileId: avatar?.media_file_id,
    photoUrl: avatar?.photo_url ?? '',
    enabled: avatar?.enabled ?? true,
    catalogKey: null,
    voiceMode: voiceModeForType(voice?.voice_type),
    voicePreset: voice?.preset_name ?? '',
    uploadMediaFileId: voice?.voice_type === 'upload' ? voice.media_file_id ?? undefined : undefined,
    uploadFileName: voice?.voice_type === 'upload' ? voice.display_name ?? 'Voice sample' : '',
    uploadSampleUrl: voice?.voice_type === 'upload' ? voice.sample_audio_url ?? '' : '',
    recordMediaFileId: voice?.voice_type === 'record' ? voice.media_file_id ?? undefined : undefined,
  }
}

interface Props {
  brandKitId?: number
  avatar?: BrandKitAvatar
  draft?: AvatarDraft
  onAdd?: (draft: AvatarDraft) => void
  onSave?: (draft: AvatarDraft) => void
}

export const AddAvatarModal = ({ brandKitId, avatar, draft, onAdd, onSave }: Props) => {
  const { closeModal } = useModals()
  const isEdit = !!avatar
  const isDraftEdit = !!draft
  const isDraftAdd = !!onAdd && !isEdit && !isDraftEdit
  const startOnForm = isEdit || isDraftEdit
  const initialVoice = avatar?.voice
  const initial = draft ? initialFromDraft(draft) : initialFromAvatar(avatar)
  const [step, setStep] = useState<'choose' | 'catalog' | 'form'>(startOnForm ? 'form' : 'choose')

  const [name, setName] = useState(initial.name)
  const [personaNote, setPersonaNote] = useState(initial.personaNote)
  const [age, setAge] = useState(initial.age)
  const [ethnicity, setEthnicity] = useState(initial.ethnicity)
  const [mediaFileId, setMediaFileId] = useState<number | undefined>(initial.mediaFileId)
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(initial.photoUrl)
  const [enabled, setEnabled] = useState(initial.enabled)
  const [isUploading, setIsUploading] = useState(false)
  const [catalogKey, setCatalogKey] = useState<string | null>(initial.catalogKey)

  const [voiceMode, setVoiceMode] = useState<VoiceMode>(initial.voiceMode)
  const [voicePreset, setVoicePreset] = useState(initial.voicePreset)
  const [uploadMediaFileId, setUploadMediaFileId] = useState<number | undefined>(initial.uploadMediaFileId)
  const [uploadFileName, setUploadFileName] = useState(initial.uploadFileName)
  const [uploadSampleUrl, setUploadSampleUrl] = useState(initial.uploadSampleUrl)
  const [recordMediaFileId, setRecordMediaFileId] = useState<number | undefined>(initial.recordMediaFileId)
  const [isVoiceUploading, setIsVoiceUploading] = useState(false)
  const [previewingKey, setPreviewingKey] = useState<string | null>(null)
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)

  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false)
  const { data: voicePresets = [] } = useAvatarVoicePresets({ brandKitId })
  const { data: catalogEntries = [] } = useAvatarCatalog({ brandKitId })
  const createAvatar = useCreateBrandKitAvatar({ brandKitId }, { onSuccess: () => closeModal() })
  const updateAvatar = useUpdateBrandKitAvatar({ brandKitId }, { onSuccess: () => closeModal() })

  useEffect(() => {
    return () => {
      previewAudioRef.current?.pause()
    }
  }, [])

  const stopPreview = () => {
    previewAudioRef.current?.pause()
    previewAudioRef.current = null
    setPreviewingKey(null)
  }

  const handlePreview = (key: string, url: string) => {
    if (previewingKey === key) {
      stopPreview()
      return
    }
    previewAudioRef.current?.pause()
    const audio = new Audio(url)
    previewAudioRef.current = audio
    audio.onended = () => setPreviewingKey(null)
    const playing = audio.play() as Promise<void> | undefined
    playing?.catch(() => setPreviewingKey(null))
    setPreviewingKey(key)
  }

  const handlePhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploading(true)
    try {
      const uploaded = await uploadImageFilesWithoutLoadingToast([file], uploadsProjectId)
      const mediaFile = uploaded?.[0]
      if (mediaFile?.media_file_id) {
        setMediaFileId(mediaFile.media_file_id)
        setPhotoPreviewUrl(mediaFile.source_url ?? '')
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleVoiceFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsVoiceUploading(true)
    try {
      const uploaded = await uploadImageFilesWithoutLoadingToast([file], uploadsProjectId)
      const mediaFile = uploaded?.[0]
      if (mediaFile?.media_file_id) {
        setUploadMediaFileId(mediaFile.media_file_id)
        setUploadFileName(mediaFile.title ?? file.name)
        setUploadSampleUrl(mediaFile.source_url ?? '')
      }
    } finally {
      setIsVoiceUploading(false)
    }
  }

  const sampleMediaFileId = voiceMode === 'record' ? recordMediaFileId : uploadMediaFileId
  const voiceConfigured = voiceMode === 'preset' ? !!voicePreset : !!sampleMediaFileId
  const hasPhoto = catalogKey ? true : !!mediaFileId
  const canSubmit = !!name.trim() && hasPhoto && voiceConfigured && !isUploading && !isVoiceUploading

  const handleCatalogPick = (entry: AvatarCatalogEntry) => {
    setName(entry.name)
    setPersonaNote(entry.persona_note)
    setAge(entry.age)
    setEthnicity(entry.ethnicity)
    setVoiceMode('preset')
    setVoicePreset(entry.voice.preset_name)
    setPhotoPreviewUrl(entry.photo_url)
    setCatalogKey(entry.key)
    setStep('form')
  }

  const handleSubmit = () => {
    const voiceSampleUrl =
      voiceMode === 'preset'
        ? voicePresets.find((preset) => preset.key === voicePreset)?.sample_url
        : uploadSampleUrl || undefined

    if (onSave && draft) {
      onSave({
        ...draft,
        name: name.trim(),
        personaNote: personaNote.trim(),
        age: age || undefined,
        ethnicity: ethnicity || undefined,
        catalogKey: catalogKey ?? undefined,
        mediaFileId: catalogKey ? undefined : mediaFileId,
        photoUrl: photoPreviewUrl,
        voicePreset: voiceMode === 'preset' ? voicePreset : null,
        voiceMediaFileId: voiceMode === 'preset' ? null : sampleMediaFileId ?? null,
        voiceSampleUrl: voiceSampleUrl ?? draft.voiceSampleUrl,
      })
      closeModal()
      return
    }

    if (onAdd && !isEdit) {
      onAdd({
        localId: catalogKey ?? `upload-${mediaFileId}`,
        name: name.trim(),
        personaNote: personaNote.trim(),
        age: age || undefined,
        ethnicity: ethnicity || undefined,
        catalogKey: catalogKey ?? undefined,
        mediaFileId: catalogKey ? undefined : mediaFileId,
        photoUrl: photoPreviewUrl,
        voicePreset: voiceMode === 'preset' ? voicePreset : null,
        voiceMediaFileId: voiceMode === 'preset' ? null : sampleMediaFileId ?? null,
        voiceSampleUrl,
      })
      closeModal()
      return
    }

    const baseFields = {
      name: name.trim(),
      persona_note: personaNote.trim(),
      age: age || undefined,
      ethnicity: ethnicity || undefined,
      enabled,
      voice_preset: voiceMode === 'preset' ? voicePreset : null,
      voice_media_file_id: voiceMode === 'preset' ? null : sampleMediaFileId ?? null,
    }

    if (isEdit) {
      updateAvatar.mutate({ id: avatar.id, media_file_id: mediaFileId, ...baseFields })
      return
    }

    if (catalogKey) {
      createAvatar.mutate({ catalog_key: catalogKey, ...baseFields })
      return
    }

    if (!mediaFileId) return
    createAvatar.mutate({ media_file_id: mediaFileId, ...baseFields })
  }

  if (step === 'choose') {
    return (
      <Modal.Root size="lg" blaze>
        <Modal.Header
          title="Add an avatar"
          subHeader="Pick a ready-made avatar to customize, or start blank and upload your own photo to build from."
          onClose={closeModal}
        />
        <Modal.Content>
          <div className={Styles.choices}>
            <button type="button" className={Styles.choiceCard} onClick={() => setStep('catalog')}>
              <span className={Styles.choiceIcon}>
                <UserProfileAdd size={20} />
              </span>
              <span className={Styles.choiceText}>
                <Text fontWeight="500">Choose from our avatars</Text>
                <Text variant="secondary">Browse a library of ready-made avatars and tailor one to your brand.</Text>
              </span>
            </button>
            <button
              type="button"
              className={`${Styles.choiceCard} ${Styles.choiceCardDashed}`}
              onClick={() => setStep('form')}
            >
              <span className={Styles.choiceIcon}>
                <Upload size={20} />
              </span>
              <span className={Styles.choiceText}>
                <Text fontWeight="500">Start blank — upload your own</Text>
                <Text variant="secondary">Upload a photo of the avatar you want, then describe it.</Text>
              </span>
            </button>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="right">
            <Button variant="tertiary" size="lg" onPress={closeModal}>
              Cancel
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )
  }

  if (step === 'catalog') {
    return (
      <Modal.Root size="lg" blaze>
        <Modal.Header
          title="Choose an avatar"
          subHeader="Pick a ready-made face and voice, then tailor the details to your brand."
          onClose={closeModal}
        />
        <Modal.Content>
          <div className={Styles.catalogGrid}>
            {catalogEntries.map((entry) => (
              <div key={entry.key} className={Styles.catalogCard}>
                <div className={Styles.catalogPoster}>
                  <img className={Styles.catalogPhoto} src={entry.photo_url} alt={entry.name} />
                  <button
                    type="button"
                    className={Styles.catalogPlayButton}
                    aria-label={`Preview ${entry.name} voice`}
                    onClick={() => handlePreview(entry.key, entry.voice.sample_audio_url)}
                  >
                    {previewingKey === entry.key ? <Pause /> : <Play3 fill="currentColor" />}
                  </button>
                </div>
                <div className={Styles.catalogInfo}>
                  <Text fontWeight="500">{entry.name}</Text>
                  <Text variant="secondary" className={Styles.catalogPersona}>
                    {entry.persona_note}
                  </Text>
                </div>
                <button
                  type="button"
                  className={Styles.catalogSelectButton}
                  aria-label={`Choose ${entry.name}`}
                  onClick={() => handleCatalogPick(entry)}
                />
              </div>
            ))}
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" size="lg" onPress={() => setStep('choose')}>
              Back
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )
  }

  const renderVoicePresetPicker = () => {
    const selectedPreset = voicePresets.find((preset) => preset.key === voicePreset)

    return (
      <div className={Styles.voicePresetRow}>
        <div className={Styles.voicePresetSelect}>
          <Select
            aria-label="Voice preset"
            variant="secondary"
            size="lg"
            useFullWidth
            placeholder="Select a voice"
            selectedKey={voicePreset || null}
            onSelectionChange={(key) => setVoicePreset(String(key))}
          >
            {voicePresets.map((preset) => (
              <Item key={preset.key} textValue={voicePresetLabel(preset)}>
                {voicePresetLabel(preset)}
              </Item>
            ))}
          </Select>
        </div>
        <IconButton
          icon={previewingKey === voicePreset ? Pause : Play3}
          size="sm"
          variant="tertiary"
          isDisabled={!selectedPreset}
          onPress={() => selectedPreset && handlePreview(selectedPreset.key, selectedPreset.sample_url)}
          aria-label="Preview voice"
        />
      </div>
    )
  }

  const renderVoiceUpload = () => {
    if (isVoiceUploading) {
      return (
        <div className={Styles.voiceUploading} data-testid="voice-upload-loading">
          <Spinner variant="dark" />
          <Text variant="secondary">Uploading…</Text>
        </div>
      )
    }

    if (uploadMediaFileId) {
      return (
        <div className={Styles.voiceRow}>
          <Text variant="smallList">{uploadFileName}</Text>
          {!!uploadSampleUrl && (
            <IconButton
              icon={previewingKey === 'uploaded-sample' ? Pause : Play3}
              size="sm"
              variant="tertiary"
              onPress={() => handlePreview('uploaded-sample', uploadSampleUrl)}
              aria-label="Preview voice sample"
            />
          )}
          <label className={Styles.voiceReplace}>
            <Text variant="secondary">Replace</Text>
            <input
              type="file"
              accept="audio/mpeg"
              onChange={handleVoiceFileChange}
              aria-label="Replace voice sample"
              hidden
            />
          </label>
        </div>
      )
    }

    return (
      <label className={Styles.voiceDropzone}>
        <span className={Styles.dropzoneIcon}>
          <Upload size={20} />
        </span>
        <Text fontWeight="500">Upload a voice sample</Text>
        <Text variant="secondary">MP3 only</Text>
        <input
          type="file"
          accept="audio/mpeg"
          onChange={handleVoiceFileChange}
          aria-label="Upload a voice sample"
          hidden
        />
      </label>
    )
  }

  const renderDropzoneContent = () => {
    if (isUploading) {
      return (
        <div className={Styles.uploading} data-testid="upload-loading">
          <Spinner variant="dark" />
          <Text variant="secondary">Uploading…</Text>
        </div>
      )
    }

    if (photoPreviewUrl) {
      return <img src={photoPreviewUrl} alt="Avatar preview" />
    }

    return (
      <>
        <span className={Styles.dropzoneIcon}>
          <Upload size={20} />
        </span>
        <Text fontWeight="500">Upload a photo of your avatar</Text>
        <Text variant="secondary">JPG or PNG</Text>
      </>
    )
  }

  return (
    <Modal.Root size="lg" blaze>
      <Modal.Header title={formTitle(startOnForm, isDraftAdd)} onClose={closeModal} />
      <Modal.Content>
        <div className={Styles.formLayout}>
          <div className={Styles.uploadColumn}>
            {catalogKey ? (
              <div className={Styles.lockedPhoto}>
                <img src={photoPreviewUrl} alt="Avatar" />
              </div>
            ) : (
              <label className={Styles.dropzone}>
                {renderDropzoneContent()}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handlePhotoChange}
                  aria-label="Upload a photo of your avatar"
                  disabled={isUploading}
                  hidden
                />
              </label>
            )}
            {isEdit && avatar.verification_status === 'rejected' && (
              <div className={Styles.rejectedCallout}>
                <Text variant="smallList" color="var(--red-70)">
                  {verificationFailureCopy(avatar)}
                </Text>
              </div>
            )}
          </div>
          <div className={Styles.fields}>
            <div className={Styles.field}>
              <Text variant="smallList">Name</Text>
              <Input name="avatar-name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
            </div>
            <div className={Styles.field}>
              <Text variant="smallList">Persona</Text>
              <Input
                name="avatar-persona"
                value={personaNote}
                onChange={(e) => setPersonaNote(e.target.value)}
                placeholder="e.g. Quiet craftsman, Austin trades roots"
                aria-label="Persona"
              />
            </div>
            <div className={Styles.appearance}>
              <div className={Styles.selectRow}>
                <div className={Styles.field}>
                  <Text variant="smallList">Age</Text>
                  <Select
                    aria-label="Age"
                    variant="secondary"
                    size="lg"
                    useFullWidth
                    placeholder="Select age"
                    selectedKey={age || null}
                    onSelectionChange={(key) => setAge(String(key))}
                  >
                    {REPRESENTATION_AGE_OPTIONS.map((option) => (
                      <Item key={option.key} textValue={option.title}>
                        {option.title}
                      </Item>
                    ))}
                  </Select>
                </div>
                <div className={Styles.field}>
                  <Text variant="smallList">Ethnicity</Text>
                  <Select
                    aria-label="Ethnicity"
                    variant="secondary"
                    size="lg"
                    useFullWidth
                    placeholder="Select ethnicity"
                    selectedKey={ethnicity || null}
                    onSelectionChange={(key) => setEthnicity(String(key))}
                  >
                    {REPRESENTATION_ETHNICITY_OPTIONS.map((option) => (
                      <Item key={option.key} textValue={option.title}>
                        {option.title}
                      </Item>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div className={Styles.voiceSection}>
              <Text variant="smallList">Voice</Text>
              <SegmentedControl
                variant="primary"
                activeItem={voiceMode}
                onItemClick={(key) => {
                  const mode = key as VoiceMode
                  if (mode !== voiceMode) setRecordMediaFileId(undefined)
                  setVoiceMode(mode)
                }}
                size="sm"
              >
                <Item key="preset" textValue="Preset voice">
                  Preset voice
                </Item>
                <Item key="upload" textValue="Upload sample">
                  Upload sample
                </Item>
                <Item key="record" textValue="Record">
                  Record
                </Item>
              </SegmentedControl>
              {voiceMode === 'preset' && renderVoicePresetPicker()}
              {voiceMode === 'upload' && renderVoiceUpload()}
              {voiceMode === 'record' && (
                <VoiceRecorder
                  uploadsProjectId={uploadsProjectId}
                  initialMediaFileId={
                    initialVoice?.voice_type === 'record' ? initialVoice.media_file_id ?? undefined : undefined
                  }
                  initialSampleUrl={
                    initialVoice?.voice_type === 'record' ? initialVoice.sample_audio_url ?? undefined : undefined
                  }
                  onChange={(recordedMediaFileId) => setRecordMediaFileId(recordedMediaFileId ?? undefined)}
                />
              )}
            </div>
            <div className={Styles.toggleRow}>
              <div className={Styles.toggleText}>
                <Text variant="smallList">Include in content rotation</Text>
                <Text variant="secondary" color="var(--dark-60)">
                  When off, this avatar will be skipped during generation.
                </Text>
              </div>
              <div
                className={Styles.toggleSwitch}
                role="switch"
                aria-checked={enabled}
                aria-label="Include in content rotation"
                tabIndex={0}
                onClick={() => setEnabled(!enabled)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEnabled(!enabled)
                  }
                }}
              >
                <VisualSwitch active={enabled} variant="md" />
              </div>
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" onPress={closeModal}>
            Cancel
          </Button>
          {!startOnForm && catalogKey && (
            <Button variant="tertiary" size="lg" onPress={() => setStep('catalog')}>
              Back
            </Button>
          )}
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            variant="primary"
            size="lg"
            onPress={handleSubmit}
            isDisabled={!canSubmit || createAvatar.isLoading || updateAvatar.isLoading}
          >
            {submitLabel(startOnForm, isDraftAdd)}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
